import type { HoverActionItem } from "@promptlayer/ui";
import { adaptContent, Message as SharedMessage } from "@promptlayer/ui";
import { DatabaseIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { MessageAvatar } from "@/components/Playground/Drawer/Chat/ChatMessage/MessageAvatar";
import { JsonBubble } from "@/components/RequestDisplay/ChatView/JsonBubble";
import { TraceTranscriptMessageActions } from "@/components/RequestDisplay/ChatView/TraceTranscriptMessageActions";
import {
  getJsonText,
  getMessageBubbleClassName,
} from "@/components/RequestDisplay/ChatView/display-utils";
import { TraceMessageBlock } from "@/components/RequestDisplay/ChatView/utils";
import { BasicTooltip } from "@/components/ui/Tooltip";
import type { CacheControl, Content, ToolCall } from "@/types";

function getCacheLabel(cc: CacheControl | undefined): string | undefined {
  if (!cc) {
    return undefined;
  }
  return cc.ttl === "1h" ? "1h" : "5m";
}

function getMessageCacheLabels(
  content: Content[],
  messageCacheControl?: CacheControl,
  toolCalls?: ToolCall[],
): string[] {
  const labels: string[] = [];
  for (const block of content) {
    if (block.type === "text" && "cache_control" in block) {
      const label = getCacheLabel(
        (block as Content & { cache_control?: CacheControl }).cache_control,
      );
      if (label) {
        labels.push(label);
      }
    }
  }
  if (messageCacheControl) {
    const label = getCacheLabel(messageCacheControl);
    if (label) {
      labels.push(label);
    }
  }
  if (toolCalls) {
    for (const tc of toolCalls) {
      if (tc.cache_control) {
        const label = getCacheLabel(tc.cache_control);
        if (label) {
          labels.push(label);
        }
      }
    }
  }
  return labels;
}

function getInlineCollapseFadeClassName(role: TraceMessageBlock["displayMessage"]["role"]) {
  switch (role) {
    case "user":
      return "via-blue-500/85 to-blue-500";
    case "tool":
    case "function":
      return "via-amber-50/85 to-amber-50";
    case "system":
      return "via-slate-50/85 to-slate-50";
    case "developer":
      return "via-sky-50/85 to-sky-50";
    case "placeholder":
      return "via-gray-50/85 to-gray-50";
    case "assistant":
    default:
      return "via-gray-100/85 to-gray-100";
  }
}

export const TranscriptMessageBlock = ({ block }: { block: TraceMessageBlock }) => {
  const [isJsonExpanded, setIsJsonExpanded] = useState(false);
  const [isMessageExpanded, setIsMessageExpanded] = useState(false);
  const [isMessageExpandable, setIsMessageExpandable] = useState(false);
  const isRightAligned = block.displayMessage.role === "user";
  const sharedRole =
    block.displayMessage.role === "user" || block.displayMessage.role === "tool"
      ? block.displayMessage.role
      : "assistant";
  const rawLabel =
    block.displayMessage.raw_request_display_role || block.displayMessage.role || "assistant";
  const avatarLabel = rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1);
  const jsonText = useMemo(() => getJsonText(block.jsonValue), [block.jsonValue]);
  const messageContent = useMemo(
    () => adaptContent(block.displayMessage.content ?? []),
    [block.displayMessage.content],
  );
  const toolCalls =
    block.displayMessage.role === "assistant" ? block.displayMessage.tool_calls : undefined;
  const toolCallId =
    "tool_call_id" in block.displayMessage ? block.displayMessage.tool_call_id : undefined;

  const messageCacheControl =
    "cache_control" in block.displayMessage
      ? (block.displayMessage.cache_control as CacheControl | undefined)
      : undefined;

  const cacheLabels = useMemo(
    () => getMessageCacheLabels(block.displayMessage.content ?? [], messageCacheControl, toolCalls),
    [block.displayMessage.content, messageCacheControl, toolCalls],
  );

  const handleToggleMessageExpansion = useCallback(() => {
    setIsMessageExpanded(current => !current);
  }, []);

  const handleToggleJson = useCallback(() => {
    setIsJsonExpanded(current => !current);
  }, []);

  const extraHoverActions: HoverActionItem[] = [
    ...(cacheLabels.length > 0
      ? [
          {
            key: "cache-badge",
            node: (
              <BasicTooltip content="Prompt cache breakpoint">
                <span className="flex items-center gap-0.5 rounded border border-blue-300 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-600">
                  <DatabaseIcon className="h-3 w-3" />
                  {cacheLabels.join(", ")}
                </span>
              </BasicTooltip>
            ),
          },
        ]
      : []),
    {
      key: "view-json",
      node: (
        <TraceTranscriptMessageActions
          align={isRightAligned ? "end" : "start"}
          isJsonExpanded={isJsonExpanded}
          isMessageExpanded={isMessageExpanded}
          showMessageExpansionToggle={isMessageExpandable && !isJsonExpanded}
          onToggleMessageExpansion={handleToggleMessageExpansion}
          onToggleJson={handleToggleJson}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col">
      {isJsonExpanded ? (
        <JsonBubble
          isRightAligned={isRightAligned}
          jsonText={jsonText}
          hoverActions={extraHoverActions}
        />
      ) : (
        <SharedMessage
          id={block.id}
          role={sharedRole}
          content={messageContent}
          toolCalls={toolCalls}
          name={block.displayMessage.name}
          toolCallId={toolCallId}
          avatar={!isRightAligned ? <MessageAvatar label={avatarLabel} /> : undefined}
          extraHoverActions={extraHoverActions}
          bubbleClassName={getMessageBubbleClassName(block.displayMessage)}
          hideCopyButton={false}
          collapsibleBody={{
            isExpanded: isMessageExpanded,
            onToggle: handleToggleMessageExpansion,
            onOverflowChange: setIsMessageExpandable,
            fadeClassName: getInlineCollapseFadeClassName(block.displayMessage.role),
          }}
          disableAnimation
        />
      )}
    </div>
  );
};
